import React, { useState } from 'react';
import {
  MagnifyingGlassIcon,
  FunnelIcon,
  EyeIcon,
  PencilIcon,
  PhoneIcon,
  EnvelopeIcon,
  AcademicCapIcon,
  CalendarIcon,
  ChevronDownIcon
} from '@heroicons/react/24/outline';
import { Student, CourseHistory, FilterOptions } from '../../types';

const StudentManagement: React.FC = () => {
  const [searchTerm, setSearchTerm] = useState('');
  const [selectedSemester, setSelectedSemester] = useState('all');
  const [selectedYear, setSelectedYear] = useState('all');
  const [selectedStudent, setSelectedStudent] = useState<Student | null>(null);
  const [showDetailModal, setShowDetailModal] = useState(false);

  // 임시 데이터
  const [students] = useState<Student[]>([
    {
      id: '1',
      name: '김민수',
      email: 'minsu.kim@example.com',
      phoneNumber: '010-1234-5678',
      birthDate: '1995-03-15',
      gender: 'male',
      address: '경기도 동두천시 생연동',
      registrationDate: '2023-06-15',
      lastLoginDate: '2024-12-10',
      totalCourses: 12,
      completedCourses: 10,
      currentCourses: 2,
      courseHistory: [
        {
          courseId: '1',
          courseName: '비즈니스 영어회화',
          instructor: '김영희',
          semester: '2024-2',
          year: 2024,
          startDate: '2024-09-01',
          endDate: '2024-12-31',
          status: 'in-progress',
          attendance: 85,
          tuition: 150000,
          paymentStatus: 'completed'
        },
        {
          courseId: '2',
          courseName: '토익 집중반',
          instructor: '이민수',
          semester: '2024-1',
          year: 2024,
          startDate: '2024-03-01',
          endDate: '2024-06-30',
          status: 'completed',
          grade: 'A',
          attendance: 95,
          tuition: 200000,
          paymentStatus: 'completed'
        }
      ]
    },
    {
      id: '2',
      name: '이지은',
      email: 'jieun.lee@example.com',
      phoneNumber: '010-2345-6789',
      birthDate: '1998-07-22',
      gender: 'female',
      address: '경기도 동두천시 중앙동',
      registrationDate: '2024-01-10',
      lastLoginDate: '2024-12-09',
      totalCourses: 5,
      completedCourses: 3,
      currentCourses: 2,
      courseHistory: [
        {
          courseId: '3',
          courseName: '어린이 영어교실',
          instructor: '박지은',
          semester: '2024-2',
          year: 2024,
          startDate: '2024-09-01',
          endDate: '2024-12-31',
          status: 'in-progress',
          attendance: 90,
          tuition: 120000,
          paymentStatus: 'completed'
        }
      ]
    },
    {
      id: '3',
      name: '박준호',
      email: 'junho.park@example.com',
      phoneNumber: '010-3456-7890',
      birthDate: '2000-11-08',
      gender: 'male',
      address: '경기도 동두천시 보산동',
      registrationDate: '2023-09-20',
      lastLoginDate: '2024-12-08',
      totalCourses: 8,
      completedCourses: 7,
      currentCourses: 1,
      courseHistory: []
    }
  ]);

  // 필터링된 학생 목록
  const filteredStudents = students.filter(student => {
    const matchesSearch =
      student.name.toLowerCase().includes(searchTerm.toLowerCase()) ||
      student.email.toLowerCase().includes(searchTerm.toLowerCase()) ||
      student.phoneNumber.includes(searchTerm);

    let matchesSemester = selectedSemester === 'all';
    let matchesYear = selectedYear === 'all';

    if (student.courseHistory && student.courseHistory.length > 0) {
      if (selectedSemester !== 'all') {
        matchesSemester = student.courseHistory.some(
          course => course.semester === selectedSemester
        );
      }
      if (selectedYear !== 'all') {
        matchesYear = student.courseHistory.some(
          course => course.year === parseInt(selectedYear)
        );
      }
    }

    return matchesSearch && matchesSemester && matchesYear;
  });

  // 학생 상세 정보 보기
  const handleViewDetails = (student: Student) => {
    setSelectedStudent(student);
    setShowDetailModal(true);
  };

  // 수강 상태 라벨
  const getStatusLabel = (status: string) => {
    const labels: { [key: string]: { text: string; color: string } } = {
      'in-progress': { text: '수강중', color: 'bg-blue-100 text-blue-800' },
      'completed': { text: '수료', color: 'bg-green-100 text-green-800' },
      'dropped': { text: '중도포기', color: 'bg-red-100 text-red-800' }
    };
    return labels[status] || { text: status, color: 'bg-gray-100 text-gray-800' };
  };

  return (
    <div>
      <div className="mb-8">
        <h1 className="text-3xl font-bold text-gray-800 mb-2">수강생 관리</h1>
        <p className="text-gray-600">등록된 수강생을 조회하고 관리합니다.</p>
      </div>

      {/* 검색 및 필터 */}
      <div className="bg-white rounded-lg shadow mb-6 p-4">
        <div className="flex flex-col lg:flex-row gap-4">
          <div className="flex-1 relative">
            <MagnifyingGlassIcon className="absolute left-3 top-1/2 -translate-y-1/2 h-5 w-5 text-gray-400" />
            <input
              type="text"
              placeholder="이름, 이메일, 전화번호로 검색..."
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              className="w-full pl-10 pr-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
            />
          </div>

          <div className="flex items-center gap-4">
            <div className="flex items-center gap-2">
              <FunnelIcon className="h-5 w-5 text-gray-500" />
              <span className="text-sm text-gray-600">필터:</span>
            </div>

            <select
              value={selectedYear}
              onChange={(e) => setSelectedYear(e.target.value)}
              className="px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
            >
              <option value="all">전체 연도</option>
              <option value="2024">2024년</option>
              <option value="2023">2023년</option>
              <option value="2022">2022년</option>
            </select>

            <select
              value={selectedSemester}
              onChange={(e) => setSelectedSemester(e.target.value)}
              className="px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
            >
              <option value="all">전체 학기</option>
              <option value="2024-2">2024년 2학기</option>
              <option value="2024-1">2024년 1학기</option>
              <option value="2023-2">2023년 2학기</option>
              <option value="2023-1">2023년 1학기</option>
            </select>
          </div>
        </div>
      </div>

      {/* 수강생 목록 테이블 */}
      <div className="bg-white rounded-lg shadow overflow-hidden">
        <div className="overflow-x-auto">
          <table className="min-w-full divide-y divide-gray-200">
            <thead className="bg-gray-50">
              <tr>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  수강생 정보
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  연락처
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  수강 현황
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  현재 수강중
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  가입일
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  관리
                </th>
              </tr>
            </thead>
            <tbody className="bg-white divide-y divide-gray-200">
              {filteredStudents.map((student) => (
                <tr key={student.id} className="hover:bg-gray-50">
                  <td className="px-6 py-4 whitespace-nowrap">
                    <div className="flex items-center">
                      <div className="flex-shrink-0 h-10 w-10 bg-blue-100 rounded-full flex items-center justify-center">
                        <span className="text-blue-600 font-semibold">
                          {student.name.charAt(0)}
                        </span>
                      </div>
                      <div className="ml-4">
                        <div className="text-sm font-medium text-gray-900">
                          {student.name}
                        </div>
                        <div className="text-sm text-gray-500">
                          {student.gender === 'male' ? '남' : student.gender === 'female' ? '여' : '-'}
                          {student.birthDate && ` · ${new Date().getFullYear() - new Date(student.birthDate).getFullYear()}세`}
                        </div>
                      </div>
                    </div>
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap">
                    <div className="text-sm">
                      <div className="flex items-center text-gray-900 mb-1">
                        <EnvelopeIcon className="h-4 w-4 mr-1 text-gray-400" />
                        {student.email}
                      </div>
                      <div className="flex items-center text-gray-600">
                        <PhoneIcon className="h-4 w-4 mr-1 text-gray-400" />
                        {student.phoneNumber}
                      </div>
                    </div>
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap">
                    <div className="text-sm">
                      <div className="flex items-center space-x-4">
                        <div>
                          <span className="text-gray-500">총</span>
                          <span className="font-semibold text-gray-900 ml-1">{student.totalCourses}</span>
                        </div>
                        <div>
                          <span className="text-gray-500">수료</span>
                          <span className="font-semibold text-green-600 ml-1">{student.completedCourses}</span>
                        </div>
                        <div>
                          <span className="text-gray-500">진행</span>
                          <span className="font-semibold text-blue-600 ml-1">{student.currentCourses}</span>
                        </div>
                      </div>
                    </div>
                  </td>
                  <td className="px-6 py-4">
                    {student.currentCourses > 0 ? (
                      <div className="flex flex-wrap gap-1">
                        {student.courseHistory
                          ?.filter(course => course.status === 'in-progress')
                          .slice(0, 2)
                          .map((course, idx) => (
                            <span
                              key={idx}
                              className="inline-flex items-center px-2 py-1 rounded-full text-xs font-medium bg-blue-100 text-blue-800"
                            >
                              {course.courseName.length > 10
                                ? course.courseName.substring(0, 10) + '...'
                                : course.courseName}
                            </span>
                          ))}
                        {student.courseHistory && student.courseHistory.filter(course => course.status === 'in-progress').length > 2 && (
                          <span className="text-xs text-gray-500">
                            +{student.courseHistory.filter(course => course.status === 'in-progress').length - 2}
                          </span>
                        )}
                      </div>
                    ) : (
                      <span className="text-sm text-gray-500">수강중인 강좌 없음</span>
                    )}
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                    {new Date(student.registrationDate).toLocaleDateString('ko-KR')}
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap text-sm font-medium">
                    <div className="flex space-x-2">
                      <button
                        onClick={() => handleViewDetails(student)}
                        className="text-indigo-600 hover:text-indigo-900"
                        title="상세보기"
                      >
                        <EyeIcon className="h-5 w-5" />
                      </button>
                      <button
                        className="text-blue-600 hover:text-blue-900"
                        title="수정"
                      >
                        <PencilIcon className="h-5 w-5" />
                      </button>
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>

        {filteredStudents.length === 0 && (
          <div className="text-center py-12">
            <p className="text-gray-500">검색 결과가 없습니다.</p>
          </div>
        )}
      </div>

      {/* 상세 정보 모달 */}
      {showDetailModal && selectedStudent && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-lg max-w-4xl w-full max-h-[90vh] overflow-y-auto">
            <div className="sticky top-0 bg-white border-b border-gray-200 px-6 py-4">
              <div className="flex justify-between items-start">
                <h2 className="text-2xl font-bold text-gray-900">수강생 상세 정보</h2>
                <button
                  onClick={() => setShowDetailModal(false)}
                  className="text-gray-400 hover:text-gray-600"
                >
                  <svg className="h-6 w-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M6 18L18 6M6 6l12 12" />
                  </svg>
                </button>
              </div>
            </div>

            <div className="p-6">
              {/* 기본 정보 */}
              <div className="mb-8">
                <h3 className="text-lg font-semibold text-gray-900 mb-4">기본 정보</h3>
                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <label className="block text-sm font-medium text-gray-500">이름</label>
                    <p className="mt-1 text-base text-gray-900">{selectedStudent.name}</p>
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-500">이메일</label>
                    <p className="mt-1 text-base text-gray-900">{selectedStudent.email}</p>
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-500">전화번호</label>
                    <p className="mt-1 text-base text-gray-900">{selectedStudent.phoneNumber}</p>
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-500">생년월일</label>
                    <p className="mt-1 text-base text-gray-900">
                      {selectedStudent.birthDate
                        ? new Date(selectedStudent.birthDate).toLocaleDateString('ko-KR')
                        : '-'}
                    </p>
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-500">주소</label>
                    <p className="mt-1 text-base text-gray-900">{selectedStudent.address || '-'}</p>
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-500">가입일</label>
                    <p className="mt-1 text-base text-gray-900">
                      {new Date(selectedStudent.registrationDate).toLocaleDateString('ko-KR')}
                    </p>
                  </div>
                </div>
              </div>

              {/* 수강 통계 */}
              <div className="mb-8">
                <h3 className="text-lg font-semibold text-gray-900 mb-4">수강 통계</h3>
                <div className="grid grid-cols-3 gap-4">
                  <div className="bg-gray-50 rounded-lg p-4 text-center">
                    <p className="text-2xl font-bold text-gray-900">{selectedStudent.totalCourses}</p>
                    <p className="text-sm text-gray-500">전체 수강</p>
                  </div>
                  <div className="bg-green-50 rounded-lg p-4 text-center">
                    <p className="text-2xl font-bold text-green-600">{selectedStudent.completedCourses}</p>
                    <p className="text-sm text-gray-500">수료 완료</p>
                  </div>
                  <div className="bg-blue-50 rounded-lg p-4 text-center">
                    <p className="text-2xl font-bold text-blue-600">{selectedStudent.currentCourses}</p>
                    <p className="text-sm text-gray-500">현재 수강중</p>
                  </div>
                </div>
              </div>

              {/* 수강 이력 */}
              <div>
                <h3 className="text-lg font-semibold text-gray-900 mb-4">수강 이력</h3>
                {selectedStudent.courseHistory && selectedStudent.courseHistory.length > 0 ? (
                  <div className="space-y-3">
                    {selectedStudent.courseHistory.map((course, idx) => (
                      <div key={idx} className="border border-gray-200 rounded-lg p-4">
                        <div className="flex justify-between items-start mb-2">
                          <div>
                            <h4 className="font-semibold text-gray-900">{course.courseName}</h4>
                            <p className="text-sm text-gray-500">강사: {course.instructor}</p>
                          </div>
                          <span className={`px-2 py-1 text-xs font-semibold rounded-full ${
                            getStatusLabel(course.status).color
                          }`}>
                            {getStatusLabel(course.status).text}
                          </span>
                        </div>
                        <div className="grid grid-cols-2 md:grid-cols-4 gap-2 text-sm">
                          <div>
                            <span className="text-gray-500">학기:</span>
                            <span className="ml-1 text-gray-900">{course.semester}</span>
                          </div>
                          <div>
                            <span className="text-gray-500">기간:</span>
                            <span className="ml-1 text-gray-900">
                              {new Date(course.startDate).toLocaleDateString('ko-KR', { month: '2-digit', day: '2-digit' })}
                              ~
                              {new Date(course.endDate).toLocaleDateString('ko-KR', { month: '2-digit', day: '2-digit' })}
                            </span>
                          </div>
                          <div>
                            <span className="text-gray-500">출석률:</span>
                            <span className="ml-1 text-gray-900">{course.attendance}%</span>
                          </div>
                          <div>
                            <span className="text-gray-500">수강료:</span>
                            <span className="ml-1 text-gray-900">{course.tuition.toLocaleString()}원</span>
                          </div>
                        </div>
                      </div>
                    ))}
                  </div>
                ) : (
                  <p className="text-gray-500 text-center py-8">수강 이력이 없습니다.</p>
                )}
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default StudentManagement;